import React, { useEffect, useRef, useState } from 'react';
import { firestoreService } from '../shared/services/firestoreService';
import { USE_FIREBASE } from '../shared/services/apiConfig';
import { Button, Input } from '../shared/design';
import { userMessage } from '../shared/userFacingError';
import {
    MIN_PASSWORD_LENGTH,
    REJECTED_APPLICATION_RETENTION_LABEL,
    RESTAURATEUR_CONTACT_EMAIL,
    RESTAURATEUR_SIGNUP_FIELDS,
    RESTAURATEUR_TERMS_URL,
    validateRestaurateurSignup,
    type RestaurateurSignupField,
    type RestaurateurSignupForm as FormValues,
    type RestaurateurSignupMode,
} from '../shared/restaurateurApplication';

const EMPTY_FORM: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    restaurantName: '',
    address: '',
    siret: '',
    acceptedTerms: false,
};

type FieldErrors = Partial<Record<RestaurateurSignupField, string>>;

const fieldId = (field: RestaurateurSignupField) => `restaurateur-${field}`;

interface Props {
    /** `signup` : crée le compte ; `existingAccount` : client déjà connecté. */
    mode?: RestaurateurSignupMode;
    /** E-mail du compte connecté (mode `existingAccount`). */
    accountEmail?: string;
    /** Envoi en cours : l'écran parent bloque sa navigation (onglets…). */
    onBusyChange?: (busy: boolean) => void;
    /** Mode `existingAccount` : retour à l'écran précédent. */
    onCancel?: () => void;
}

/**
 * Demande d'accès restaurateur : à l'inscription (crée le compte CLIENT et la
 * demande) ou depuis un compte client existant. Une fois l'écriture confirmée
 * par le serveur, App / RestaurateurApplicationGate affichent l'écran d'attente :
 * ce composant n'a rien d'autre à faire. En cas d'échec, il reste affiché avec
 * le message d'erreur.
 */
const RestaurateurSignupForm: React.FC<Props> = ({ mode = 'signup', accountEmail, onBusyChange, onCancel }) => {
    const isSignup = mode === 'signup';
    const [form, setForm] = useState<FormValues>(EMPTY_FORM);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    // Focus sur le premier champ en erreur (ou le bandeau d'erreur), après affichage.
    const [focusRequest, setFocusRequest] = useState(0);
    const submitErrorRef = useRef<HTMLDivElement>(null);
    const mounted = useRef(true);

    // Remis à true à chaque montage (StrictMode démonte puis remonte en développement).
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        onBusyChange?.(isLoading);
    }, [isLoading]);
    useEffect(() => () => onBusyChange?.(false), []);

    useEffect(() => {
        if (focusRequest === 0) return;
        const first = RESTAURATEUR_SIGNUP_FIELDS.find((field) => errors[field]);
        if (first) document.getElementById(fieldId(first))?.focus();
        else submitErrorRef.current?.focus();
    }, [focusRequest]);

    const setField = <K extends RestaurateurSignupField>(field: K, value: FormValues[K]) => {
        setForm((f) => ({ ...f, [field]: value }));
        if (errors[field]) {
            setErrors((current) => {
                const next = { ...current };
                delete next[field];
                return next;
            });
        }
    };

    const textProps = (field: Exclude<RestaurateurSignupField, 'acceptedTerms'>) => ({
        id: fieldId(field),
        name: field,
        value: form[field],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setField(field, e.target.value),
        error: errors[field],
        required: true,
        fullWidth: true,
        disabled: isLoading,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        const result = validateRestaurateurSignup(form, isSignup ? 'signup' : 'existingAccount');
        if (result.ok === false) {
            setErrors(result.errors);
            setFocusRequest((n) => n + 1);
            return;
        }
        if (!USE_FIREBASE) return; // bouton désactivé en mode démo
        setIsLoading(true);
        try {
            if (isSignup) {
                await firestoreService.registerRestaurateurApplicant(result.values);
            } else {
                const { email: _email, password: _password, ...input } = result.values;
                await firestoreService.submitRestaurateurApplication(input);
            }
            // Écriture confirmée : l'écran « Demande reçue » remplace ce formulaire.
            if (mounted.current) setIsLoading(false);
        } catch (err) {
            if (!mounted.current) return;
            const message = userMessage(err, "Votre demande n'a pas pu être envoyée. Réessayez dans quelques instants.");
            const field = (err as { field?: RestaurateurSignupField }).field;
            if (field) {
                setErrors({ [field]: message });
            } else {
                setSubmitError(message);
            }
            setFocusRequest((n) => n + 1);
            setIsLoading(false);
        }
    };

    const termsError = errors.acceptedTerms;
    const contactText = RESTAURATEUR_CONTACT_EMAIL ? (
        <>en écrivant à <a href={`mailto:${encodeURIComponent(RESTAURATEUR_CONTACT_EMAIL)}`} className="underline">{RESTAURATEUR_CONTACT_EMAIL}</a></>
    ) : (
        <>en contactant l'équipe atable</>
    );

    return (
        <form className="space-y-6" onSubmit={handleSubmit} noValidate aria-label="Demande d'accès restaurateur">
            {!USE_FIREBASE && (
                <div className="bg-info-bg text-info border border-cream-200 px-4 py-3 rounded-xl text-sm font-medium" role="note">
                    Mode démo : la demande d'accès restaurateur n'est pas disponible. Utilisez l'accès rapide
                    « Restaurateur » depuis l'onglet Connexion.
                </div>
            )}

            <p className="text-sm text-gray-600">
                {isSignup ? 'Créez votre compte : notre' : 'Notre'} équipe vérifie les informations de votre
                établissement avant d'ouvrir votre espace restaurateur. Tous les champs sont obligatoires.
            </p>

            <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-gray-900 mb-1">Vous</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Prénom" type="text" autoComplete="given-name" placeholder="Jean" {...textProps('firstName')} />
                    <Input label="Nom" type="text" autoComplete="family-name" placeholder="Dupont" {...textProps('lastName')} />
                </div>
                {isSignup ? (
                    <>
                        <Input label="Adresse e-mail" type="email" autoComplete="email" placeholder="jean@monrestaurant.fr" {...textProps('email')} />
                        <Input
                            label="Mot de passe"
                            type="password"
                            autoComplete="new-password"
                            helperText={`Au moins ${MIN_PASSWORD_LENGTH} caractères.`}
                            {...textProps('password')}
                        />
                    </>
                ) : (
                    <p className="text-sm text-gray-700">
                        Compte : <span className="font-medium">{accountEmail}</span>
                    </p>
                )}
                <Input
                    label="Téléphone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="06 12 34 56 78"
                    helperText="Pour vous joindre pendant la vérification. Numéro étranger : commencez par + et l'indicatif du pays."
                    {...textProps('phone')}
                />
            </fieldset>

            <fieldset className="space-y-4">
                <legend className="text-sm font-semibold text-gray-900 mb-1">Votre établissement</legend>
                <Input label="Nom du restaurant" type="text" autoComplete="organization" placeholder="Chez Jean" {...textProps('restaurantName')} />
                <Input
                    label="Adresse du restaurant"
                    type="text"
                    autoComplete="street-address"
                    placeholder="12 rue de la Paix, 75002 Paris"
                    {...textProps('address')}
                />
                <Input
                    label="Numéro SIRET"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="123 456 789 00012"
                    helperText="14 chiffres, sur votre extrait Kbis ou votre avis de situation Insee."
                    {...textProps('siret')}
                />
            </fieldset>

            <div>
                <div className="flex items-start gap-3">
                    <input
                        id={fieldId('acceptedTerms')}
                        name="acceptedTerms"
                        type="checkbox"
                        checked={form.acceptedTerms}
                        onChange={(e) => setField('acceptedTerms', e.target.checked)}
                        disabled={isLoading}
                        required
                        aria-invalid={termsError ? true : undefined}
                        aria-describedby={[termsError ? `${fieldId('acceptedTerms')}-message` : '', 'restaurateur-privacy']
                            .filter(Boolean).join(' ')}
                        className="mt-1 w-4 h-4 accent-brand-500 cursor-pointer"
                    />
                    <label htmlFor={fieldId('acceptedTerms')} className="text-sm text-gray-700 cursor-pointer">
                        {RESTAURATEUR_TERMS_URL ? (
                            <>
                                J'accepte les{' '}
                                <a href={RESTAURATEUR_TERMS_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 underline hover:text-brand-700">
                                    conditions d'utilisation professionnelles<span className="sr-only"> (nouvel onglet)</span>
                                </a>{' '}
                                d'atable et certifie que les informations saisies sont exactes.
                            </>
                        ) : (
                            <>
                                Je certifie que les informations saisies sont exactes et j'accepte qu'atable les
                                utilise pour étudier ma demande d'accès professionnel.
                            </>
                        )}
                    </label>
                </div>
                {termsError && (
                    <p id={`${fieldId('acceptedTerms')}-message`} className="mt-1.5 text-sm text-red-600">
                        {termsError}
                    </p>
                )}
            </div>

            {submitError && (
                <div
                    ref={submitErrorRef}
                    tabIndex={-1}
                    role="alert"
                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                    {submitError}
                </div>
            )}

            <div className={onCancel ? 'flex flex-col-reverse sm:flex-row gap-3' : ''}>
                {onCancel && (
                    <Button type="button" variant="outline" size="lg" fullWidth onClick={onCancel} disabled={isLoading}>
                        Annuler
                    </Button>
                )}
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isLoading} disabled={!USE_FIREBASE}>
                    Envoyer ma demande
                </Button>
            </div>

            <p id="restaurateur-privacy" className="text-xs text-gray-600 leading-relaxed">
                Vos informations servent uniquement à étudier votre demande d'accès professionnel. Elles sont
                conservées jusqu'à la décision, puis {REJECTED_APPLICATION_RETENTION_LABEL} en cas de refus (avec
                votre compte en cas d'acceptation). Vous pouvez y accéder ou demander leur suppression {contactText} ;
                tant que la demande est en attente, vous pouvez aussi la retirer vous-même.
            </p>
        </form>
    );
};

export default RestaurateurSignupForm;
